import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import { SettingContext } from "../SettingContext";
import {
    LottieLoadingIndicator,
    Text,
    FlexContainer,
    Toast,
    CardHeadingLabel,
    ExplainParagraphLabel,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalText
} from "src/components";

import CompanyPaymentMethods from "src/scenes/Subscription/api/CompanyPaymentMethods";

import CardGrid from "src/scenes/Setting/components/CardGrid";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import PaymentIcons from "src/img/payment-icons.png";

const SubText = styled(Text)`
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: ${ p => p.weight || 400 };
    text-align: ${p => p.align || "left"};
    color: ${ p => p.color };
    font-size: ${p => p.size || "14px"};
    margin: ${ p => p.margin || 0 };
`;

function Payments() {
    const { email, token, companyName, abn } = useContext(SettingContext);
    const [cardLoadings, setCardLoadings] = useState(0);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentMethodListKey, setPaymentMethodListKey] = useState(0);
    const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = useState(false);
    
    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    const showAddPaymentMethodForm = () => {
        setShowAddPaymentMethodModal(true);
    };

    const hideAddPaymentMethodForm = () => {
        setShowAddPaymentMethodModal(false);
    };

    const onPaymentMethodAdded = () => {
        // Hide the form, make the payment method list regenerate
        setShowAddPaymentMethodModal(false);
        setPaymentMethodListKey(paymentMethodListKey + 1);
        fetchPaymentMethods();
    };

    const fetchPaymentMethods = async () => {
        setCardLoadings(prev => prev + 1);
        try {
            const dividedPaymentMethods = await CompanyPaymentMethods.get(token);
            setPaymentMethods(dividedPaymentMethods.intoDataArray());
        } catch (error) {
            console.log(error);
        } finally {
            setCardLoadings(prev => prev - 1);
        }
    };

    /**
     * @param {GenericPaymentMethod} item
     */
    const setPaymentMethodAsDefault = async (item) => {
        let route;
        let body;
        switch (item.source) {
        case "airwallex":
            route = `${API_URL}/company/airwallex/paymentMethods/default`;
            body = { paymentMethodId: item.id };
            break;
        case "square":
            route = `${API_URL}/company/airwallex/paymentMethods/default`;
            body = { paymentMethodId: item.id };
            break;
        default:
            console.log("Invalid source: " + item.source);
            return;
        }

        setCardLoadings(prev => prev + 1);
        try {
            await axios.post(route, body, {
                headers: {
                    Authorization: "Bearer " + token
                }
            });
            setCardLoadings(prev => prev - 1);
            await fetchPaymentMethods();
        } catch (error) {
            setCardLoadings(prev => prev - 1);
            Toast.error(error.getApiErrorDetails().message);
            console.log(error);
        }
    };

    /**
     * @param {GenericPaymentMethod} item
     */
    const deletePaymentMethod = async (item) => {
        let route;
        switch (item.source) {
        case "airwallex":
            route = `${API_URL}/company/airwallex/paymentMethods/${item.id}`;
            break;
        case "square":
            route = `${API_URL}/company/square/card/${item.id}`;
            break;
        default:
            console.log("Invalid source: " + item.source);
            return;
        }

        setCardLoadings(prev => prev + 1);
        try {
            await axios.delete(route, {
                headers: {
                    Authorization: "Bearer " + token
                }
            });
            setCardLoadings(prev => prev - 1);
            await fetchPaymentMethods();
        } catch (error) {
            setCardLoadings(prev => prev - 1);
            Toast.error(error.getApiErrorDetails().message);
            
            console.log(error);
        }
    };

    return (
        <FlexContainer width="100%" maxWidth="54rem" height="100%">
            <FlexContainer marginBottom="2em">
                <CardHeadingLabel>
                        Payment Methods
                </CardHeadingLabel>
                <ExplainParagraphLabel>
                        View and manage the payment methods in your account.
                </ExplainParagraphLabel>
                <SubText weight={700} margin="1rem 0 0" color="black">Your Account Payment Methods</SubText>
            </FlexContainer>

            { cardLoadings > 0 ?
                <LottieLoadingIndicator /> :
                <>
                    <React.Fragment key={paymentMethodListKey}>

                        <FlexContainer width="100%" flexGrow="1" margin="0 auto" justifyContent="left">
                            <CardGrid
                                paymentMethods={paymentMethods}
                                onSetDefaultClicked={item => setPaymentMethodAsDefault(item)}
                                onDeleteClicked={item => deletePaymentMethod(item)}
                                onAddPaymentMethodClicked={ () => showAddPaymentMethodForm() } />
                        </FlexContainer>
                    </React.Fragment>
                        
                </>
            }
            <StandardModal
                open={showAddPaymentMethodModal}
                onClose={hideAddPaymentMethodForm}
                title="Add a Payment Method"
                widthMode="min(35rem, calc(100vw - 2rem))"
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton onClick={hideAddPaymentMethodForm}>
                            Cancel
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                )}
            >
                <StandardModalSection>
                    <StandardModalText>
                        <img src={PaymentIcons} alt="Payment Icons" height="34px" />
                    </StandardModalText>
                    {showAddPaymentMethodModal && (
                        <AirwallexForm
                            token={ token }
                            email={ email }
                            backToPrevious={ () => hideAddPaymentMethodForm() }
                            nextClick={ () => onPaymentMethodAdded() }
                            companyName={ companyName }
                            abn={abn}
                            productType={AirwallexProduct.SAVE_NEW_PAYMENT_METHOD}
                            numberOfUsers={0}
                        />
                    )}
                </StandardModalSection>
            </StandardModal>
        </FlexContainer>
    );
}

export default Payments;
